/** Parse "lat,lng" vehicle location strings from the API. */
export const parseVehicleCoordinates = (vehicleLocation) => {
  if (!vehicleLocation || typeof vehicleLocation !== "string") return null;

  const parts = vehicleLocation.split(",").map((part) => parseFloat(part.trim()));
  if (parts.length !== 2 || parts.some((n) => Number.isNaN(n))) return null;

  const [lat, lng] = parts;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

  return { lat, lng };
};

/** Haversine distance in kilometres. */
export const haversineDistanceKm = (lat1, lng1, lat2, lng2) => {
  const toRadians = (degrees) => degrees * (Math.PI / 180);
  const earthRadiusKm = 6371;

  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
};

/** Same rules as API search availability (pending/accept block dates). */
export const bookingBlocksAvailability = (booking, rangeStart, rangeEnd) => {
  if (!booking) return false;

  const type = String(booking.type || "").toLowerCase();
  const status = String(booking.status || "").toLowerCase();

  if (type === "decline" || status === "canceled") return false;
  if (status === "complete") return false;
  if (!["pending", "accept"].includes(type)) return false;

  const bStart = new Date(booking.StartDate);
  const bEnd = new Date(booking.EndDate);
  const s = new Date(rangeStart);
  const e = new Date(rangeEnd);

  if (Number.isNaN(bStart.getTime()) || Number.isNaN(bEnd.getTime())) return false;

  return bStart <= e && bEnd >= s;
};

/** Bookings that can still hide this vehicle from customer search. */
export const getBlockingBookings = (bookings, rangeStart, rangeEnd) => {
  if (!Array.isArray(bookings)) return [];

  return bookings.filter((booking) =>
    bookingBlocksAvailability(booking, rangeStart, rangeEnd)
  );
};

/** Geocode a city/place label via OpenStreetMap Nominatim (no API key). */
export const geocodePlaceName = async (placeName) => {
  const query = String(placeName || "").trim();
  if (!query) return null;

  const url = new URL("https://nominatim.openstreetmap.org/search");
  url.searchParams.set("q", query);
  url.searchParams.set("format", "json");
  url.searchParams.set("limit", "1");

  const response = await fetch(url.toString(), {
    headers: {
      Accept: "application/json",
      "Accept-Language": "en",
    },
  });

  if (!response.ok) return null;

  const results = await response.json();
  if (!Array.isArray(results) || results.length === 0) return null;

  const lat = parseFloat(results[0].lat);
  const lng = parseFloat(results[0].lon);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

  return {
    lat,
    lng,
    label: results[0].display_name || query,
  };
};

export const SEARCH_RADIUS_KM = 200;

export const buildGoogleStaticMapUrl = (lat, lng, apiKey, width = 800, height = 320) => {
  if (!apiKey) return null;

  const params = new URLSearchParams({
    center: `${lat},${lng}`,
    zoom: "14",
    size: `${width}x${height}`,
    scale: "2",
    maptype: "roadmap",
    markers: `color:0x22a3f1|${lat},${lng}`,
    key: apiKey,
  });

  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
};

/** OpenStreetMap embed — no API key. */
export const buildOsmEmbedUrl = (lat, lng, delta = 0.02) => {
  const left = lng - delta;
  const right = lng + delta;
  const top = lat + delta;
  const bottom = lat - delta;
  const bbox = [left, bottom, right, top].map((n) => n.toFixed(6)).join("%2C");
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat.toFixed(6)}%2C${lng.toFixed(6)}`;
};

/**
 * Google Maps embed (no API key). More reliable in admin iframes than OSM alone.
 * Prefer this for the visible preview; OSM remains as a fallback src list option.
 */
export const buildGoogleMapsEmbedUrl = (lat, lng, zoom = 14) =>
  `https://maps.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}&z=${zoom}&output=embed`;

/** @deprecated staticmap.openstreetmap.de is offline — prefer embed URLs. */
export const buildOsmStaticMapUrl = (lat, lng, width = 800, height = 320) => {
  void lat;
  void lng;
  void width;
  void height;
  return null;
};

export const buildOpenInMapsUrl = (lat, lng) =>
  `https://www.google.com/maps?q=${lat},${lng}`;
